import { EnvConfig } from '@config';
import { DueDatesDTO, GAS_INJECTOR, WATER_INJECTOR } from 'pages/WellGuardian/WIR/wir.constants';
import queryString from 'query-string';
import { ApiError, ApiResponse } from 'types/apiResponse';
import {
  Alert,
  AlertsCount,
  AnnuliGauge,
  AnnulusStatus,
  AssetType,
  BudgetCG,
  ChecklistQuestions,
  CheckScheduleCalculateDTO,
  CheckScheduleResponseDTO,
  CommentEditWIR,
  CommentWIR,
  Derogation,
  DgsHse,
  EstimatedGain,
  fetchWellData,
  GetPiWells,
  GetSchematics,
  GetSchematicsNonUK,
  GetWell,
  InjectionRate,
  Integrity,
  IntegrityPopor,
  IntegrityStatus,
  Intervention,
  KeyLinkD,
  LatestWellPotentials,
  LivePerformance,
  PauseCalculateDTO,
  PausePersistenceDTO,
  PIIntegrityPlots,
  PrimaveraType,
  ProductionRate,
  R2SLink,
  riskStatus,
  ScheduleWIR,
  SiteLayout,
  SiteType,
  SubscriptionDTO,
  SubscriptionType,
  SynergiCase,
  SynergiCaseCommon,
  TargetInfo,
  tmoreDTO,
  UserDTO,
  WellBehaviour,
  WellBehaviourActivity,
  WellBehaviourChoke,
  WellBehaviourClusterStats,
  WellCache,
  WellCount,
  WellCreate,
  WellCreateLive,
  WellCreateLiveUK,
  WellCreatePI,
  WellInfo,
  WellOppurtunitiesFetch,
  WellResources,
  WellReviews,
  WellReviewsFetch,
  WellSchematic,
  WellSchematicOther,
  WHTemperaturePressure,
  WIR,
  WIRAssignIntervationSpdt,
  WIRDTO,
  WIREngineeringAnalysis,
  WirEOIForm,
  WIREquipmentIdentification,
  WirInfoFetch,
  WIRInitialRequestInformation,
  WIRIWOP,
  WIRListPresentation,
  WIRManagerialSummary,
  WIROrganiseKickOffMeeting,
  WIRPhaseTypes,
  WIRSChecksActionsSectionDTO,
  WIRSectionDTO,
  WIRSignOffDTO,
  WIRUser,
  WIRWellInformation,
} from 'types/wellGuardian';

import Client from './client';

const wellGuardianApiBaseUrl = EnvConfig.drillx_wg_api_url || '';
const accept = 'application/json';

class WellGuardianApiClient {
  client: Client;

  constructor() {
    this.client = new Client(wellGuardianApiBaseUrl, accept);
  }

  getAzureBlobFile = (
    filename: string,
    schematicType: string,
    tuwi: string,
    country: string,
  ): Promise<Blob> => {
    return this.client.getBlob(
      `/azure-blob-storage/schematics/${tuwi}/${country}/${encodeURIComponent(
        filename,
      )}/${encodeURIComponent(schematicType)}`,
    );
  };

  getWellSchematicByTuwi = async (
    tuwi: string,
    country: string,
  ): Promise<ApiResponse<WellSchematic[]>> => {
    try {
      const response = await this.client.get(`/well-schematic/${tuwi}/${country}`);

      return { response };
    } catch (error: any) {
      return { error, response: [] };
    }
  };

  getWellSchematicByTuwiOther = async (
    tuwi: string,
    country: string,
  ): Promise<ApiResponse<WellSchematicOther[]>> => {
    try {
      const response = await this.client.get(`/well-schematic/${tuwi}/${country}`);

      return { response };
    } catch (error: any) {
      return { error, response: [] };
    }
  };

  getWellInfoByTuwi = async (
    tuwi: string,
    country: string,
  ): Promise<ApiResponse<WellInfo | null>> => {
    try {
      const response = await this.client.get(`/well/${tuwi}/well-info/${country}`);

      return { response };
    } catch (error: any) {
      return { error, response: null };
    }
  };

  getResourcesByTuwi = async (tuwi: string): Promise<WellResources[]> => {
    try {
      const response = await this.client.get(`/well/${tuwi}/resources`);

      return response;
    } catch {
      return [];
    }
  };

  createResources = async (
    tuwi: string,
    createResourceDto: Partial<WellResources>,
  ): Promise<WellResources | null> => {
    try {
      const createResources = await this.client.post(`/well/${tuwi}/resources`, createResourceDto);

      return createResources;
    } catch {
      return null;
    }
  };

  deleteResources = async (tuwi: string, id: string | undefined): Promise<boolean> => {
    try {
      await this.client.delete(`/well/${tuwi}/resources/${id}`);

      return true;
    } catch {
      return false;
    }
  };

  createPastInterv = async (
    country: string,
    createPastIntervDto: Partial<Intervention>,
  ): Promise<Intervention | null> => {
    try {
      const createPastInterv = await this.client.post(
        `/well/${country}/interventions/create`,
        createPastIntervDto,
      );

      return createPastInterv;
    } catch {
      return null;
    }
  };

  getReviewsByTuwi = async (tuwi: string): Promise<WellReviewsFetch[]> => {
    try {
      const response = await this.client.get(`/well/${tuwi}/well-reviews`);

      return response;
    } catch {
      return [];
    }
  };

  getOpportunitiesByTuwi = async (tuwi: string): Promise<WellOppurtunitiesFetch[]> => {
    try {
      const response = await this.client.get(`/popor/${tuwi}/Opportunities`);

      return response;
    } catch {
      return [];
    }
  };

  getReviewsByCountry = async (country: string): Promise<WellReviewsFetch[]> => {
    try {
      const response = await this.client.get(`/well/well-reviews/${country}`);

      return response;
    } catch {
      return [];
    }
  };

  createReviews = async (
    tuwi: string,
    createResourceDto: Partial<WellReviews>,
  ): Promise<WellReviews | null> => {
    try {
      const createResources = await this.client.post(
        `/well/${tuwi}/well-reviews`,
        createResourceDto,
      );

      return createResources;
    } catch {
      return null;
    }
  };

  editReviews = async (
    tuwi: string,
    id: string,
    createResourceDto: Partial<WellReviews>,
  ): Promise<WellReviews | null> => {
    try {
      const createResources = await this.client.patch(
        `/well/${tuwi}/well-reviews/${id}`,
        createResourceDto,
      );

      return createResources;
    } catch {
      return null;
    }
  };

  deleteReviews = async (tuwi: string, id: string | undefined): Promise<boolean> => {
    try {
      await this.client.delete(`/well/${tuwi}/well-reviews/${id}`);

      return true;
    } catch {
      return false;
    }
  };

  getLinksByCountry = async (country: string): Promise<KeyLinkD[]> => {
    try {
      const response = await this.client.get(`/well/${country}/keylinks`);

      return response;
    } catch {
      return [];
    }
  };

  createLinks = async (
    country: string,
    createLinkDto: Partial<KeyLinkD>,
  ): Promise<KeyLinkD | null> => {
    try {
      const createLinks = await this.client.post(`/well/${country}/keylinks`, createLinkDto);

      return createLinks;
    } catch {
      return null;
    }
  };

  editLinks = async (
    country: string,
    id: string,
    editLinkDto: Partial<KeyLinkD>,
  ): Promise<KeyLinkD | null> => {
    try {
      const editLinks = await this.client.patch(`/well/${country}/keylinks/${id}`, editLinkDto);

      return editLinks;
    } catch {
      return null;
    }
  };

  deleteLinks = async (country: string, id: string | undefined): Promise<boolean> => {
    try {
      await this.client.delete(`/well/${country}/keylinks/${id}`);

      return true;
    } catch {
      return false;
    }
  };

  getEOIData = async (wir: string | undefined): Promise<WirEOIForm | null> => {
    try {
      const response = await this.client.get(`/eoi/${wir}`);

      return response;
    } catch {
      return null;
    }
  };

  sendEOIData = async (wir: string | undefined, createEOIData: any): Promise<WirEOIForm | null> => {
    try {
      const response = await this.client.post(`/eoi/${wir}/eoi-section`, createEOIData);

      return response;
    } catch {
      return null;
    }
  };

  editEOIData = async (wir: string | undefined, createEOIData: any): Promise<WirEOIForm | null> => {
    try {
      const response = await this.client.patch(`/eoi/${wir}/eoi-section`, createEOIData);

      return response;
    } catch {
      return null;
    }
  };

  getToolTips = async (country: string): Promise<WirInfoFetch[]> => {
    try {
      const response = await this.client.get(`/wir/tools-tips/${country}`);

      return response;
    } catch {
      return [];
    }
  };

  createToolTips = async (
    country: string,
    createToolTips: Partial<WirInfoFetch>,
  ): Promise<WirInfoFetch | null> => {
    try {
      const createTips = await this.client.post(`/wir/tools-tips/${country}`, createToolTips);

      return createTips;
    } catch {
      return null;
    }
  };

  editToolTips = async (
    country: string,
    id: string,
    editToolTips: Partial<WirInfoFetch>,
  ): Promise<WirInfoFetch | null> => {
    try {
      const editTips = await this.client.patch(`/wir/tools-tips/${country}/${id}`, editToolTips);

      return editTips;
    } catch {
      return null;
    }
  };

  deleteToolTips = async (country: string, id: string | undefined): Promise<boolean> => {
    try {
      await this.client.delete(`/wir/tools-tips/${country}/${id}`);

      return true;
    } catch {
      return false;
    }
  };

  getIntegrityStatusByTuwi = async (tuwi: string): Promise<IntegrityStatus[]> => {
    try {
      const data = await this.client.get(`/well/${tuwi}/integrity-status`);

      return data;
    } catch {
      return [];
    }
  };

  getAnnulusStatusByTuwi = async (tuwi: string): Promise<AnnulusStatus[]> => {
    try {
      const data = await this.client.get(`/well/${tuwi}/annulus-status`);

      return data;
    } catch {
      return [];
    }
  };

  getAnnuliGaugesByTuwi = async (tuwi: string, country: string): Promise<AnnuliGauge[]> => {
    try {
      const data = await this.client.get(`/well/${country}/${tuwi}/annuli-gauges`);

      return data;
    } catch {
      return [];
    }
  };

  getLatestWellPotentialByTuwi = async (
    tuwi: string,
    country: string,
  ): Promise<LatestWellPotentials[]> => {
    try {
      const data = await this.client.get(`/well/${tuwi}/latest-well-potential/${country}`);

      return data;
    } catch {
      return [];
    }
  };

  getPerformanceLiveLink = async (tuwi: string, country: string): Promise<LivePerformance[]> => {
    try {
      const data = await this.client.get(`/wir/npdms/${tuwi}/${country}`);

      return data;
    } catch {
      return [];
    }
  };

  getProductionRateByTuwiAndByDate = async (
    tuwi: string,
    dateFrom: string,
    dateTo: string,
  ): Promise<ProductionRate[]> => {
    try {
      const data = await this.client.get(
        `/well/${tuwi}/production-rates?datefrom=${dateFrom}&dateto=${dateTo}`,
      );

      return data;
    } catch {
      return [];
    }
  };

  getInjectionRateByTuwiAndByDate = async (
    tuwi: string,
    dateFrom: string,
    dateTo: string,
  ): Promise<InjectionRate[]> => {
    try {
      const data = await this.client.get(
        `/well/${tuwi}/injection-rates?datefrom=${dateFrom}&dateto=${dateTo}`,
      );

      return data;
    } catch {
      return [];
    }
  };

  getWHTemperaturePressureByTuwiAndByDate = async (
    tuwi: string,
    dateFrom: string,
    dateTo: string,
    wellType: string | undefined,
  ): Promise<WHTemperaturePressure[]> => {
    try {
      const data =
        wellType === WATER_INJECTOR || wellType === GAS_INJECTOR
          ? await this.client.get(
              `/well/${tuwi}/injection-temp-pressure?datefrom=${dateFrom}&dateto=${dateTo}`,
            )
          : await this.client.get(
              `/well/${tuwi}/temp-pressure?datefrom=${dateFrom}&dateto=${dateTo}`,
            );

      return data;
    } catch {
      return [];
    }
  };

  getPIIntegrityPlotByTuwi = async (country: string, tuwi: string): Promise<PIIntegrityPlots[]> => {
    try {
      const data = await this.client.get(`/well/${country}/${tuwi}/charts`);

      return data;
    } catch {
      return [];
    }
  };

  getDerogations = async (
    siteName: SiteType,
    country: string,
    assetName: string,
  ): Promise<Derogation[]> => {
    try {
      const data = await this.client.get(
        `/derogations/${assetName}/${siteName}/${country}?activeonly=true`,
      );

      return data;
    } catch {
      return [];
    }
  };

  getInterventionsByTuwi = async (tuwi: string, country: string): Promise<Intervention[]> => {
    try {
      const data = await this.client.get(`/well/${tuwi}/${country}/interventions`);

      return data;
    } catch {
      return [];
    }
  };

  getEstimatedGainByTuwi = async (tuwi: string): Promise<Intervention[]> => {
    try {
      const data = await this.client.get(`/well/${tuwi}/interventions/gains`);

      return data;
    } catch {
      return [];
    }
  };

  createEstimatedGain = async (
    country: string,
    gainPayload: Partial<EstimatedGain>,
  ): Promise<EstimatedGain | null> => {
    try {
      const createEstimatedGain = await this.client.post(
        `/well/${country}/interventions/create`,
        gainPayload,
      );

      return createEstimatedGain;
    } catch {
      return null;
    }
  };

  updateEstimatedGain = async (
    country: string,
    gainPayload: Partial<EstimatedGain>,
  ): Promise<EstimatedGain | null> => {
    try {
      const updateEstimatedGain = await this.client.patch(
        `/well/${country}/interventions/update`,
        gainPayload,
      );

      return updateEstimatedGain;
    } catch {
      return null;
    }
  };

  getSynergiCasesTuwi = async (
    asset: AssetType,
    site: SiteType,
    country: string,
    tuwi: string,
  ): Promise<DgsHse[]> => {
    try {
      const data = await this.client.get(`/synergi-cases/${country}/${asset}/${site}?tuwi=${tuwi}`);

      return data;
    } catch {
      return [];
    }
  };

  getIntegrityTuwi = async (tuwi: string): Promise<Integrity[]> => {
    try {
      const data = await this.client.get(`/well/${tuwi}/well-integrity`);

      return data;
    } catch {
      return [];
    }
  };

  getSynergiCases = async (
    asset: AssetType,
    site: SiteType,
    country: string,
  ): Promise<SynergiCase[]> => {
    try {
      const data = await this.client.get(`/synergi-cases/${country}/${asset}/${site}`);

      return data;
    } catch {
      return [];
    }
  };

  getSynergiCasesCommon = async (
    country: string,
    asset: AssetType,
    site: SiteType,
  ): Promise<SynergiCaseCommon[]> => {
    try {
      const data = await this.client.get(`/synergi-cases/${country}/${asset}/${site}/common`);

      return data;
    } catch {
      return [];
    }
  };

  getAlertsCountByTuwi = async (tuwi: string): Promise<ApiResponse<AlertsCount | null>> => {
    try {
      const response = await this.client.get(`/well/${tuwi}/alerts-count`);

      return { response };
    } catch (error: any) {
      return { error, response: null };
    }
  };

  getPoporIntegrity = async (countrySelect: string): Promise<IntegrityPopor[]> => {
    try {
      const data = await this.client.get(`/popor/${countrySelect}/integrity`);

      return data;
    } catch {
      return [];
    }
  };

  getWellsCount = async (): Promise<WellCount[]> => {
    try {
      const data = await this.client.get(`/well/well-count`);

      return data;
    } catch {
      return [];
    }
  };

  getWellsInfoForSite = async (
    assetId: string,
    siteId: string,
    countrySelect: string,
  ): Promise<WellInfo[]> => {
    try {
      const data = await this.client.get(`/well/wells-info/${assetId}/${siteId}/${countrySelect}`);

      return data;
    } catch {
      return [];
    }
  };

  getTargetViewData = async (
    asset: string,
    site: string,
    countrySelect: string,
  ): Promise<TargetInfo[]> => {
    try {
      const data = await this.client.get(
        `/well/latest-well-potential/${asset}/${site}/${countrySelect}`,
      );

      return data;
    } catch {
      return [];
    }
  };

  getSiteLayoutsById = async (siteId: string): Promise<SiteLayout | null> => {
    try {
      const data = await this.client.get(`/well/site-layouts/${siteId}`);

      return data;
    } catch {
      return null;
    }
  };

  getAlertsByTuwi = async (tuwi: string): Promise<ApiResponse<Alert[] | null>> => {
    try {
      const response = await this.client.get(`/well/${tuwi}/alerts`);

      return { response };
    } catch (error: any) {
      return { error, response: null };
    }
  };

  getWellBehaviourByTuwiAndByDate = async (
    tuwi: string,
    datefrom: string,
    dateto: string,
    country: string,
  ): Promise<ApiResponse<WellBehaviour[]>> => {
    try {
      const response = await this.client.get(
        `/well/${tuwi}/${country}/well-behaviour?datefrom=${datefrom}&dateto=${dateto}`,
      );

      return { response };
    } catch (error: any) {
      return { error, response: [] };
    }
  };

  getWellBehaviourChokeByTuwiAndByDate = async (
    tuwi: string,
    datefrom: string,
    dateto: string,
  ): Promise<ApiResponse<WellBehaviourChoke[]>> => {
    try {
      const response = await this.client.get(
        `/well/${tuwi}/temp-pressure?datefrom=${datefrom}&dateto=${dateto}`,
      );

      return { response };
    } catch (error: any) {
      return { error, response: [] };
    }
  };

  getWellBehaviourClustersByTuwi = async (
    tuwi: string,
    country: string,
  ): Promise<ApiResponse<WellBehaviourClusterStats[]>> => {
    try {
      const response = await this.client.get(`/well/${tuwi}/${country}/well-behaviour/clusters`);

      return { response };
    } catch (error: any) {
      return { error, response: [] };
    }
  };

  getSubscriptions = async (country: string): Promise<SubscriptionDTO | null> => {
    try {
      const data = await this.client.get(`/well/my-alert-subscriptions/${country}`);

      return data;
    } catch {
      return null;
    }
  };

  createSubscription = async (
    subscription: Partial<SubscriptionDTO>,
  ): Promise<SubscriptionDTO | null> => {
    try {
      const createdSubscription = await this.client.post('/well/alert-subscription', subscription);

      return createdSubscription;
    } catch {
      return null;
    }
  };

  getAllPossibleSubscriptions = async (country: string): Promise<SubscriptionType[]> => {
    try {
      const data = await this.client.get(`/well/possible-subscriptions/${country}`);

      return data;
    } catch {
      return [];
    }
  };

  getActiveAlertsForCountry = async (
    country: string,
    fromDate: string,
    toDate: string,
  ): Promise<Alert[]> => {
    try {
      const query = queryString.stringify({ fromDate, toDate });

      return await this.client.get(`/wells/${country}/active-alerts-for-country?${query}`);
    } catch {
      return [];
    }
  };

  processAlerts = async (alertIds: string[]): Promise<Alert[] | null> => {
    try {
      const archivedAlerts = await this.client.patch('/well/archive-alerts', {
        alertIds,
      });

      return archivedAlerts;
    } catch {
      return null;
    }
  };

  unProcessAlerts = async (alertIds: string[]): Promise<Alert[] | null> => {
    try {
      const unArchivedAlerts = await this.client.patch('/well/un-archive-alerts', { alertIds });

      return unArchivedAlerts;
    } catch {
      return null;
    }
  };

  getAllAlertsByTuwis = async (
    country: string,
    fromDate: string,
    toDate: string,
  ): Promise<Alert[]> => {
    try {
      const query = queryString.stringify({ fromDate, toDate });

      return await this.client.get(`/wells/${country}/all-alerts-for-tuwis?${query}`);
    } catch {
      return [];
    }
  };

  getWellBehaviourActivityLogByTuwi = async (
    tuwi: string,
  ): Promise<ApiResponse<WellBehaviourActivity[]>> => {
    try {
      const response = await this.client.get(`/well/${tuwi}/well-behaviour/activity-log`);

      return { response };
    } catch (error: any) {
      return { error, response: [] };
    }
  };

  createWellBehaviourActivity = async (
    tuwi: string,
    activityEntry: Partial<WellBehaviourActivity>,
  ): Promise<WellBehaviourActivity | null> => {
    try {
      const createdActivity = await this.client.post(
        `/well/${tuwi}/well-behaviour/activity`,
        activityEntry,
      );

      return createdActivity;
    } catch {
      return null;
    }
  };

  deleteWellBehaviourActivity = async (tuwi: string, id: string): Promise<boolean> => {
    try {
      await this.client.delete(`/well/${tuwi}/well-behaviour/activity/${id}`);

      return true;
    } catch {
      return false;
    }
  };

  getR2SLink = async (id: string, linkType: string): Promise<R2SLink | null> => {
    try {
      const r2SLink = await this.client.get(`/well/r2s-link?id=${id}&linkType=${linkType}`);

      return r2SLink;
    } catch {
      return null;
    }
  };

  getAllWir = async (countryState: string): Promise<WIRListPresentation[]> => {
    try {
      const wirList = await this.client.get(`/wir/all/${countryState}`);

      return wirList;
    } catch {
      return [];
    }
  };

  getWirById = async (wirId: string): Promise<WIR | null> => {
    try {
      const wir = await this.client.get(`/wir/${wirId}`);

      return wir;
    } catch {
      return null;
    }
  };

  createWIR = async (wirCreateDto: WIRDTO, country: string): Promise<WIR | null> => {
    try {
      const createdWir = await this.client.post(`/wir/add/${country}`, wirCreateDto);

      return createdWir;
    } catch {
      return null;
    }
  };

  updateWIRInitialRequestInformation = async (
    wirId: string,
    initialRequestDto: WIRInitialRequestInformation,
    phase: WIRPhaseTypes,
    country: string,
  ): Promise<WIR | null> => {
    try {
      return await this.client.post(
        `/wir/${wirId}/phase/${phase}/section/${country}/INITIAL_REQUEST_INFORMATION`,
        initialRequestDto,
      );
    } catch {
      return null;
    }
  };

  updateWIRBudget = async (
    wirId: string,
    budgetDto: BudgetCG,
    country: string,
  ): Promise<WIR | null> => {
    try {
      return await this.client.post(`/wir/${wirId}/section/${country}/BUDGET`, budgetDto);
    } catch {
      return null;
    }
  };

  updateWIROrganiseKickOffMeeting = async (
    wirId: string,
    organiseKickOffMeetingDto: WIROrganiseKickOffMeeting,
    phase: WIRPhaseTypes,
    country: string,
  ): Promise<WIR | null> => {
    try {
      return await this.client.post(
        `/wir/${wirId}/phase/${phase}/section/${country}/ORGANISE_KICK_OFF_MEETING`,
        organiseKickOffMeetingDto,
      );
    } catch {
      return null;
    }
  };

  updateWIREquipmentIdentification = async (
    wirId: string,
    equipmentIdentificationDto: WIREquipmentIdentification,
    phase: WIRPhaseTypes,
    country: string,
  ): Promise<WIR | null> => {
    try {
      return await this.client.post(
        `/wir/${wirId}/phase/${phase}/section/${country}/EQUIPMENT_IDENTIFICATION`,
        equipmentIdentificationDto,
      );
    } catch {
      return null;
    }
  };

  updateWIRGenericSection = async (
    wirId: string,
    sectionGenericDto: WIRSectionDTO,
    country: string,
  ): Promise<WIR | null> => {
    try {
      const updatedWir = await this.client.post(
        `/wir/${wirId}/section/${country}`,
        sectionGenericDto,
      );

      return updatedWir;
    } catch {
      return null;
    }
  };

  updateWIRWellInformationSection = async (
    wirId: string,
    wellInformationSectionDto: WIRWellInformation,
    phase: WIRPhaseTypes,
    country: string,
  ): Promise<WIR | null> => {
    try {
      return await this.client.post(
        `/wir/${wirId}/phase/${phase}/section/${country}/WELL_INFORMATION`,
        wellInformationSectionDto,
      );
    } catch {
      return null;
    }
  };

  updateWIRWellHandoverChecklistSection = async (
    wirId: string,
    wellInformationSectionDto: WIRSectionDTO[],
    phase: WIRPhaseTypes,
    country: string,
  ): Promise<WIR | null> => {
    try {
      return await this.client.post1(
        `/wir/${wirId}/wellHandover/section/${country}?phase=${phase}`,
        wellInformationSectionDto,
      );
    } catch {
      return null;
    }
  };

  updateWIRCloseoutChecklistSection = async (
    wirId: string,
    wellInformationSectionDto: WIRSectionDTO[],
    phase: WIRPhaseTypes,
    country: string,
  ): Promise<WIR | null> => {
    try {
      return await this.client.post1(
        `/wir/${wirId}/close-out/section/${country}?phase=${phase}`,
        wellInformationSectionDto,
      );
    } catch {
      return null;
    }
  };

  updateWIRChecksActions = async (
    wirId: string,
    sectionDTO: WIRSChecksActionsSectionDTO,
  ): Promise<WIR | null> => {
    try {
      const updatedWir = await this.client.post(`/wir/${wirId}/checklist-section`, sectionDTO);

      return updatedWir;
    } catch {
      return null;
    }
  };

  wirUsersList = async (
    roleId: string,
    country?: string,
    throwOnError = false,
  ): Promise<WIRUser[]> => {
    try {
      const usersList = await this.client.get(
        `/wir/user-config/role-members/${roleId}?country=${country}`,
      );

      return usersList;
    } catch (error) {
      if (throwOnError) throw error;
      return [];
    }
  };

  addSignOff = async (wirId: string, signOffDTO: WIRSignOffDTO): Promise<WIR | null> => {
    try {
      const updatedWir = await this.client.post(`/wir/${wirId}/approvals`, signOffDTO);

      return updatedWir;
    } catch {
      return null;
    }
  };

  deleteSignOff = async (wirId: string, signOffType: string): Promise<WIR | null> => {
    try {
      const deleteWir = await this.client.delete(`/wir/${wirId}/approvals/${signOffType}`);

      return deleteWir;
    } catch {
      return null;
    }
  };

  getChecklistQs = async (country: string): Promise<ChecklistQuestions | null> => {
    try {
      const response = await this.client.get(`/wir-config/checklists/${country}`);

      return response;
    } catch {
      return null;
    }
  };

  pauseWir = async (
    wirId: string,
    pausePersistenceDTO: PausePersistenceDTO,
  ): Promise<WIR | null> => {
    try {
      const updatedWir = await this.client.post(`/wir/${wirId}/pause`, pausePersistenceDTO);

      return updatedWir;
    } catch {
      return null;
    }
  };

  calculatePauseWir = async (
    wirId: string,
    pausePersistenceDTO: PauseCalculateDTO,
  ): Promise<CheckScheduleCalculateDTO | null> => {
    try {
      const updatedWir = await this.client.post(
        `/wir/${wirId}/pause/calculate`,
        pausePersistenceDTO,
      );

      return updatedWir;
    } catch {
      return null;
    }
  };

  updateWIRManagerialSummarySection = async (
    wirId: string,
    managerialSummarySectionDto: WIRManagerialSummary,
    phase: WIRPhaseTypes,
    country: string,
  ): Promise<WIR | null> => {
    try {
      return await this.client.post(
        `/wir/${wirId}/phase/${phase}/section/${country}/MANAGERIAL_SUMMARY`,
        managerialSummarySectionDto,
      );
    } catch {
      return null;
    }
  };

  updateWIRAssignInterventionSpdtSection = async (
    wirId: string,
    enggAnalysisDto: WIRAssignIntervationSpdt,
    country: string,
  ): Promise<WIR | null> => {
    try {
      return await this.client.post(
        `/wir/${wirId}/section/${country}/assign-spdt`,
        enggAnalysisDto,
      );
    } catch {
      return null;
    }
  };

  updateWIRIWOPSection = async (
    wirId: string,
    iwopDto: WIRIWOP,
    country: string,
  ): Promise<WIR | null> => {
    try {
      return await this.client.post(`/wir/${wirId}/section/${country}/iwop`, iwopDto);
    } catch {
      return null;
    }
  };

  updateWIREngineeringAnalysisSection = async (
    wirId: string,
    assignInterventionSpdtDto: WIREngineeringAnalysis,
    country: string,
  ): Promise<WIR | null> => {
    try {
      return await this.client.post(
        `/wir/${wirId}/section/${country}/eng-analysis`,
        assignInterventionSpdtDto,
      );
    } catch {
      return null;
    }
  };

  getWellLogFile = (tuwi: string): Promise<Blob> => {
    return this.client.getBlob(`/azure-blob-storage/well-logs/${tuwi}`);
  };

  checkWirDueDates = async (
    interventionDate: string,
    interventionComplexity: string,
    expectedEnggTime: number,
  ): Promise<CheckScheduleResponseDTO | null> => {
    try {
      const data = await this.client.get(
        `/wir/check-due-dates?interventionDate=${interventionDate}&interventionComplexity=${interventionComplexity}&expectedEnggTime=${expectedEnggTime}`,
      );

      return data;
    } catch {
      return null;
    }
  };

  getTmoreData = async (wellId: string | undefined, country: string): Promise<tmoreDTO | null> => {
    try {
      const data = await this.client.get(`/well/${wellId}/${country}/well-tmore`);

      return data;
    } catch {
      return null;
    }
  };

  createWIRUser = async (userCreateDto: WIRUser, country: string): Promise<WIRUser | null> => {
    try {
      return await this.client.post(`/wir/user-config/${country}`, userCreateDto);
    } catch {
      return null;
    }
  };

  createMultipleWIRUser = async (
    userCreateDto: WIRUser,
    country: string,
  ): Promise<WIRUser | null> => {
    try {
      return await this.client.post(`/wir/user-config/batch/${country}`, userCreateDto);
    } catch {
      return null;
    }
  };

  createUserBatch = async (user: Partial<UserDTO>, country: string): Promise<UserDTO | null> => {
    try {
      const createdUser = await this.client.post(`/users/drillx/batch/${country}`, user);

      return createdUser;
    } catch {
      return null;
    }
  };

  editWIRUser = async (userEditDto: WIRUser, country: string): Promise<WIRUser | null> => {
    try {
      return await this.client.patch(`/wir/user-config/usermodify/${country}`, userEditDto);
    } catch {
      return null;
    }
  };

  deleteWIRUser = async (userId: string): Promise<boolean> => {
    try {
      await this.client.delete(`/wir/user-config/userdelete/${userId}`);

      return true;
    } catch {
      return false;
    }
  };

  getLatestWells = async (
    asset: string,
    site: string,
    country: string,
  ): Promise<WellInfo[] | null> => {
    try {
      return await this.client.get(`/well/wells-info/direct/${asset}/${site}/${country}`);
    } catch {
      return null;
    }
  };

  getWells = async (wellId: string): Promise<GetWell[] | null> => {
    try {
      return await this.client.get(`/userwells/${wellId}`);
    } catch {
      return null;
    }
  };

  getPiTagWells = async (country: string, wellId: string): Promise<GetPiWells[] | null> => {
    try {
      return await this.client.get(`/userpitags/get/${country}/${wellId}`);
    } catch {
      return null;
    }
  };

  getSharePointWells = async (tuwi: string): Promise<GetSchematicsNonUK[] | null> => {
    try {
      return await this.client.get(`/usersharepoint/all/${tuwi}`);
    } catch {
      return null;
    }
  };

  getLiveLinkWells = async (wellId: string): Promise<GetSchematics[] | null> => {
    try {
      return await this.client.get(`/userLivelinkUK/all/${wellId}`);
    } catch {
      return null;
    }
  };

  createWells = async (wellCreateDTO: WellCreate): Promise<WellCreate | null> => {
    try {
      return await this.client.post(`/userwells/add`, wellCreateDTO);
    } catch {
      return null;
    }
  };

  clearCacheWells = async (wellCcache: WellCache): Promise<WellCache | null> => {
    try {
      return await this.client.post(`/cache`, wellCcache);
    } catch {
      return null;
    }
  };

  editWells = async (wellCreateDTO: WellCreate): Promise<WellCreate | null> => {
    try {
      return await this.client.patch(`/userwells/update`, wellCreateDTO);
    } catch {
      return null;
    }
  };

  fetchWell = async (country: string): Promise<fetchWellData[] | null> => {
    try {
      return await this.client.get(`/welllist/${country}`);
    } catch {
      return null;
    }
  };

  createWellsPI = async (
    wellCreateDTO: WellCreatePI,
    country: string,
  ): Promise<WellCreatePI | null> => {
    try {
      return await this.client.post(`/userpitags/add/${country}`, wellCreateDTO);
    } catch {
      return null;
    }
  };

  editWellsPI = async (
    wellCreateDTO: WellCreatePI,
    country: string,
  ): Promise<WellCreatePI | null> => {
    try {
      return await this.client.patch(`/userpitags/update/${country}`, wellCreateDTO);
    } catch {
      return null;
    }
  };

  createWellsLiveUK = async (
    wellCreateDTO: WellCreateLiveUK,
    country: string,
  ): Promise<WellCreateLiveUK | null> => {
    try {
      return await this.client.post(`/userLivelinkUK/add/${country}`, wellCreateDTO);
    } catch {
      return null;
    }
  };

  editWellsLiveUK = async (
    wellCreateDTO: WellCreateLiveUK,
    country: string,
  ): Promise<WellCreateLiveUK | null> => {
    try {
      return await this.client.patch(`/userLivelinkUK/update/${country}`, wellCreateDTO);
    } catch {
      return null;
    }
  };

  createWellsLive = async (wellCreateDTO: WellCreateLive): Promise<WellCreateLive | null> => {
    try {
      return await this.client.post(`/usersharepoint/add`, wellCreateDTO);
    } catch {
      return null;
    }
  };

  editWellsLive = async (
    wellCreateDTO: WellCreateLive,
    country: string,
  ): Promise<WellCreateLive | null> => {
    try {
      return await this.client.patch(`/usersharepoint/update/${country}`, wellCreateDTO);
    } catch {
      return null;
    }
  };

  getSharePointTuwi = async (tuwi: string | undefined): Promise<CommentWIR[]> => {
    try {
      const response = await this.client.get(`/usersharepoint/all/${tuwi}`);

      return response;
    } catch {
      return [];
    }
  };

  getScheduleWirs = async (country: string): Promise<ScheduleWIR[]> => {
    try {
      return await this.client.get(`/wir/schedule-view/${country}?asset=ALL`);
    } catch {
      return [];
    }
  };

  getCommentWirs = async (wirId: string | undefined): Promise<CommentWIR[]> => {
    try {
      const response = await this.client.get(`/wir/${wirId}/comments`);

      return response;
    } catch {
      return [];
    }
  };

  createCommentWIR = async (
    wirID: string | undefined,
    wirCommentDto: CommentWIR,
  ): Promise<CommentWIR | null> => {
    try {
      const createCommentWIR = await this.client.post(`/wir/${wirID}/comments`, wirCommentDto);

      return createCommentWIR;
    } catch {
      return null;
    }
  };

  editCommentWIR = async (
    wirID: string | undefined,
    wirCommentDto: CommentEditWIR,
    id: string | undefined,
  ): Promise<CommentEditWIR | null> => {
    try {
      const createCommentWIR = await this.client.patch(
        `/wir/${wirID}/comments/${id}`,
        wirCommentDto,
      );

      return createCommentWIR;
    } catch {
      return null;
    }
  };

  getPendingTasks = async (country: string, wirId?: string | null): Promise<riskStatus[]> => {
    try {
      if (wirId === null) {
        const response = await this.client.get(`/wir/pending/${country}`);

        return response;
      } else {
        const response = await this.client.get(`/wir/pending/${country}?wirId=${wirId}`);

        return response;
      }
    } catch {
      return [];
    }
  };

  deleteCommentWIR = async (
    wirId: string | undefined,
    id: string | undefined,
  ): Promise<boolean> => {
    try {
      await this.client.delete(`/wir/${wirId}/comments/${id}`);

      return true;
    } catch {
      return false;
    }
  };

  unPauseInterventionOperationWIR = async (wirId: string, country: string): Promise<WIR | null> => {
    try {
      const updatedWir = await this.client.post(`/wir/${wirId}/unpause_interv_op/${country}`, {});

      return updatedWir;
    } catch {
      return null;
    }
  };

  deleteArchivedWIR = async (wirId: string | undefined, country: string): Promise<WIRDTO> => {
    try {
      const deletedWir = await this.client.delete(`/wir/${wirId}/${country}`);

      return deletedWir;
    } catch (error: any) {
      return error;
    }
  };

  createWGUser = async (user: Partial<UserDTO>): Promise<UserDTO | ApiError> => {
    try {
      const createdWGUser = await this.client.post('/users/drillx', user);

      return createdWGUser;
    } catch (error: any) {
      return error;
    }
  };

  deleteWGUser = async (igg: string): Promise<boolean> => {
    try {
      await this.client.delete(`/users/drillx/${igg}`);

      return true;
    } catch {
      return false;
    }
  };

  createWGDueDate = async (wirId: string, dueDateRequest: DueDatesDTO[]): Promise<WIR | null> => {
    try {
      return await this.client.post1(`/wir/date-change/${wirId}`, dueDateRequest);
    } catch {
      return null;
    }
  };

  getWGPrimaveraDetails = async (jobId: string): Promise<PrimaveraType[] | ApiError> => {
    try {
      return await this.client.get(`/wir/${jobId}/primaveraread`);
    } catch (error: any) {
      return error;
    }
  };
}

export default new WellGuardianApiClient();
